import React, { FC, useState } from "react";
import {
  Form,
  Checkbox,
  Select,
  Modal,
  Typography,
  Button,
  Tooltip,
  InputNumber,
  Divider,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Settings } from "../../data-types";
import styles from "./app-settings.module.css";

const {
  ["container"]: containerClassName,
  ["consent-container"]: consentContainerClassName,
  ["consent-text"]: consentTextClassName,
} = styles as any;
const { Text, Paragraph } = Typography;

export type AppSettingsProps = {
  settings: Partial<Settings>;
  onUpdateSettings: (settings: Settings) => Promise<void>;
};

const tinyMceFontOptions =
  "Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino;Calibri=Carlito; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva;";

export const AppSettings: FC<AppSettingsProps> = ({ settings, onUpdateSettings }) => {
  const {
    enableAi,
    enableTechPricing,
    enableWordExport,
    systemFont,
    enablePiiFiltering,
    showLibraryAssumptionsExclusions,
    numberOfAiSuggestionsToShow,
    showClarifyingQuestions,
  } = settings;
  const [showWordExportModal, setShowWordExportModal] = useState(false);
  const [userConsent, setUserConsent] = useState(false);

  const initialValues = {
    enableAi,
    enableTechPricing,
    enableWordExport,
    systemFont,
    enablePiiFiltering,
    showLibraryAssumptionsExclusions,
    numberOfAiSuggestionsToShow,
    showClarifyingQuestions,
  };

  const [form] = Form.useForm();

  const doOnFinish = async (values: any) => {
    await onUpdateSettings(values);
  };

  const handleWordExportChange = (checked: boolean) => {
    if (checked && !enableWordExport) {
      setShowWordExportModal(true);
      setUserConsent(false);
      form.setFieldsValue({ enableWordExport: false });
    } else if (!checked) {
      form.setFieldsValue({ enableWordExport: false });
      form.submit();
    }
  };

  const handleShowTerms = () => {
    setShowWordExportModal(true);
    setUserConsent(true);
  };

  const handleAcceptTerms = () => {
    if (userConsent) {
      form.setFieldsValue({ enableWordExport: true });
      form.submit();
      setShowWordExportModal(false);
    }
  };

  const handleDeclineTerms = () => {
    form.setFieldsValue({ enableWordExport: false });
    form.submit();
    setShowWordExportModal(false);
  };

  const selectOpts = tinyMceFontOptions
    .split(";")
    .filter((opt) => !!opt)
    .map((opt) => {
      const [name, value] = opt.split("=");
      return { value, label: name };
    });

  return (
    <div className={containerClassName}>
      <Form form={form} initialValues={initialValues} onFinish={doOnFinish}>
        {/* AI Settings */}
        <Typography.Title level={4} style={{ marginBottom: 16 }}>
          AI Features
        </Typography.Title>

        <Form.Item name="enableAi" label="Enable AI drafting in proposals" valuePropName="checked">
          <Checkbox onChange={() => form.submit()} />
        </Form.Item>

        <Form.Item
          name="enablePiiFiltering"
          label={
            <>
              Enable PII filtering for AI
              <Tooltip title="Filters out personally identifiable information, organisation names, and monetary values from all text sent to AI services">
                <InfoCircleOutlined style={{ marginLeft: 5 }} />
              </Tooltip>
            </>
          }
          valuePropName="checked"
        >
          <Checkbox onChange={() => form.submit()} />
        </Form.Item>

        <Form.Item
          name="showClarifyingQuestions"
          label={
            <>
              Show clarifying questions when creating proposals
              <Tooltip title="Enables contextual questions to enhance AI-powered features during proposal creation">
                <InfoCircleOutlined style={{ marginLeft: 5 }} />
              </Tooltip>
            </>
          }
          valuePropName="checked"
        >
          <Checkbox onChange={() => form.submit()} />
        </Form.Item>

        <Form.Item
          name="numberOfAiSuggestionsToShow"
          label="Number of AI generated assumptions and exclusions to show"
        >
          <InputNumber min={1} max={10} onChange={() => form.submit()} />
        </Form.Item>

        <Divider />

        {/* Display & Interface Settings */}
        <Typography.Title level={4} style={{ marginBottom: 16 }}>
          Display & Interface
        </Typography.Title>

        <Form.Item
          name="showLibraryAssumptionsExclusions"
          label="Show library assumptions and exclusions in the scope editor"
          valuePropName="checked"
        >
          <Checkbox onChange={() => form.submit()} />
        </Form.Item>

        <Form.Item
          getValueFromEvent={(val) => {
            const opts = tinyMceFontOptions.split(";").filter((opt) => !!opt);
            const fullOption = opts.find((opt) => opt.split("=")[1] === val);
            return fullOption;
          }}
          getValueProps={(val) => {
            return { value: val?.split("=")[0] };
          }}
          wrapperCol={{ span: 4 }}
          name="systemFont"
          label="System font"
        >
          <Select options={selectOpts ?? []} onChange={() => form.submit()}></Select>
        </Form.Item>

        <Divider />

        {/* Other Functionality */}
        <Typography.Title level={4} style={{ marginBottom: 16 }}>
          Other Functionality
        </Typography.Title>

        <Form.Item
          name="enableTechPricing"
          label="Enable technology pricing"
          valuePropName="checked"
        >
          <Checkbox onChange={() => form.submit()} />
        </Form.Item>

        <Form.Item
          name="enableWordExport"
          label={
            <span>
              Enable word export
              {enableWordExport && (
                <Button
                  type="link"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShowTerms();
                  }}
                  style={{ padding: 0, height: "auto", marginLeft: "8px" }}
                >
                  (show terms)
                </Button>
              )}
            </span>
          }
          valuePropName="checked"
        >
          <Checkbox onChange={(e) => handleWordExportChange(e.target.checked)} />
        </Form.Item>
      </Form>

      <Modal
        title="Word Export Feature – Terms of Use & Data Processing Consent"
        open={showWordExportModal}
        onOk={handleAcceptTerms}
        onCancel={handleDeclineTerms}
        okText="Accept & Enable"
        cancelText="Decline"
        okButtonProps={{ disabled: !userConsent }}
        width={700}
        maskClosable={false}
      >
        <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: "8px" }}>
          <Paragraph>
            By enabling the Word export feature in Virtual Pricing Director®, you acknowledge and
            agree to the following:
          </Paragraph>

          <ol style={{ paddingLeft: "20px", marginBottom: "16px" }}>
            <li style={{ marginBottom: "12px" }}>
              <Text strong>Third-party Processing:</Text> Word document conversion is performed via
              ConvertAPI, an ISO 27001-certified and GDPR-compliant third-party provider. All
              document processing occurs on servers located within the European Economic Area (EEA),
              and all data transfers are encrypted in transit.
            </li>

            <li style={{ marginBottom: "12px" }}>
              <Text strong>Data Handling:</Text> Files sent to ConvertAPI are processed only
              temporarily for the sole purpose of document conversion. They are not stored,
              retained, or accessed beyond the scope of the conversion operation. Files are deleted
              immediately upon completion of processing, in accordance with ConvertAPI's data
              handling and retention policy.
            </li>

            <li style={{ marginBottom: "12px" }}>
              <Text strong>Confidentiality & Compliance:</Text> A binding confidentiality agreement
              (NDA) is in place between Virtual Pricing Director® and ConvertAPI to ensure the
              safeguarding of your data. ConvertAPI does not access or use your files for any
              purpose other than conversion.
            </li>

            <li style={{ marginBottom: "12px" }}>
              <Text strong>User Consent:</Text> Use of the Word export feature is strictly optional.
              By choosing to enable it, you explicitly consent to the processing of your data by
              ConvertAPI in accordance with these terms and ConvertAPI's compliance standards (see:{" "}
              <a
                href="https://www.convertapi.com/compliance"
                target="_blank"
                rel="noopener noreferrer"
              >
                ConvertAPI Compliance Overview
              </a>
              ). You may withdraw your consent and deactivate the feature at any time.
            </li>

            <li style={{ marginBottom: "12px" }}>
              <Text strong>Audit Trail:</Text> Your consent and any withdrawal of your consent,
              including your user ID and the date/time of activation, will be securely stored by
              Virtual Pricing Director® to maintain an audit record.
            </li>
          </ol>

          <Paragraph>
            If you do not consent to these terms, please refrain from using the Word export
            function.
          </Paragraph>

          <div className={consentContainerClassName}>
            <Checkbox checked={userConsent} onChange={(e) => setUserConsent(e.target.checked)}>
              <span className={consentTextClassName}>
                I confirm that I understand these terms, I agree to them on behalf of my
                organisation and I confirm that I have authority to give consent on behalf of my
                organisation.
              </span>
            </Checkbox>
          </div>
        </div>
      </Modal>
    </div>
  );
};
